import { useState, useEffect } from 'react';
import api from '../services/api';
import Table, { Column } from '../components/Table';
import StatusBadge from '../components/StatusBadge';
import Select from '../components/Select';

interface Change {
  id: string;
  employee_name: string;
  employee_code: string;
  field_name: string;
  old_value: string;
  new_value: string;
  status: string;
  request_date: string;
}

export default function StaffPersonalChanges() {
  const [changes, setChanges] = useState<Change[]>([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const fetchChanges = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/personal-changes', { params: statusFilter ? { status: statusFilter } : {} });
      setChanges(res.data.changes || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const act = async (id: string, action: 'approve' | 'reject') => {
    try {
      await api.put(`/hr/personal-changes/${id}/${action}`);
      fetchChanges();
    } catch (error: any) {
      alert(error.response?.data?.message || `Failed to ${action}`);
    }
  };

  const columns: Column<Change>[] = [
    { key: 'employee_name', header: 'Employee' },
    { key: 'field_name', header: 'Field' },
    { key: 'old_value', header: 'Old Value', render: (r) => <span className="text-gray-500 line-through">{r.old_value || '(empty)'}</span> },
    { key: 'new_value', header: 'New Value', render: (r) => <span className="font-medium text-green-700">{r.new_value}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'request_date', header: 'Requested', render: (r) => new Date(r.request_date).toLocaleString() },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'pending' ? (
          <div className="flex gap-2">
            <button className="text-green-700 hover:underline text-sm" onClick={() => act(r.id, 'approve')}>
              Approve
            </button>
            <button className="text-red-600 hover:underline text-sm" onClick={() => act(r.id, 'reject')}>
              Reject
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Staff Personal — Approve/Reject</h1>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
            { value: '', label: 'All' },
          ]}
        />
      </div>
      <Table columns={columns} rows={changes} loading={loading} emptyMessage="No profile change requests" />
    </div>
  );
}
